import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { jobsApi, surveysApi, damageApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { toast } from 'sonner';
import { 
  MapPin, Camera, Check, ChevronLeft, Save, Send, 
  AlertTriangle, CheckCircle, XCircle, HelpCircle 
} from 'lucide-react';

const CONDITION_RATINGS = [
  { id: 'good', label: 'Good', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  { id: 'fair', label: 'Fair', color: 'bg-yellow-100 text-yellow-800', icon: HelpCircle },
  { id: 'poor', label: 'Poor', color: 'bg-orange-100 text-orange-800', icon: AlertTriangle },
  { id: 'damaged', label: 'Damaged', color: 'bg-red-100 text-red-800', icon: XCircle },
  { id: 'not_inspected', label: 'Not Inspected', color: 'bg-gray-100 text-gray-600', icon: HelpCircle },
];

const SEVERITY_LEVELS = [
  { id: 'none', label: 'None', color: 'bg-green-100 text-green-800' },
  { id: 'light', label: 'Light Damage', color: 'bg-yellow-100 text-yellow-800' },
  { id: 'heavy', label: 'Heavy Damage', color: 'bg-red-100 text-red-800' },
];

const DAMAGE_TYPES = [
  'flood', 'fire', 'storm', 'smoke', 'escape_of_water', 
  'accidental_damage', 'subsidence', 'pet_damage', 'vandalism'
];

export default function MobileSurveyPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const [job, setJob] = useState(null);
  const [survey, setSurvey] = useState(null);
  const [damageDescriptions, setDamageDescriptions] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('info');
  const [summary, setSummary] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [conclusion, setConclusion] = useState('');

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const jobData = await jobsApi.getOne(jobId);
      setJob(jobData);

      // Try to get existing survey or create new one
      try {
        const surveyData = await surveysApi.getByJob(jobId);
        setSurvey(surveyData);
        setSummary(surveyData.summary || '');
        setRecommendations(surveyData.recommendations || '');
        setConclusion(surveyData.conclusion || '');
      } catch {
        // Create new survey if doesn't exist
        if (jobData.survey_template_id) {
          const newSurvey = await surveysApi.create(jobId, jobData.survey_template_id);
          setSurvey(newSurvey);
        }
      }

      // Load damage descriptions
      const descriptions = await damageApi.getDescriptions();
      setDamageDescriptions(descriptions);
    } catch (error) {
      toast.error('Failed to load survey data');
    } finally {
      setIsLoading(false);
    }
  };

  const handleElementUpdate = async (elementId, field, value) => {
    if (!survey) return;

    const element = survey.elements.find(e => e.element_id === elementId);
    const updateData = { ...element, [field]: value };

    // Auto-generate description if damage_type and severity are set
    if (field === 'severity' || field === 'damage_type') {
      const damageType = field === 'damage_type' ? value : element?.damage_type;
      const severity = field === 'severity' ? value : element?.severity;
      
      if (damageType && severity && damageDescriptions[damageType]?.[elementId]?.[severity]) {
        updateData.auto_description = damageDescriptions[damageType][elementId][severity];
      }
    }

    try {
      const updated = await surveysApi.updateElement(survey.id, elementId, updateData);
      setSurvey(updated);
    } catch (error) {
      toast.error('Failed to update element');
    }
  };

  const handleGetLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          await surveysApi.updateGps(survey.id, position.coords.latitude, position.coords.longitude);
          toast.success('GPS location captured');
          fetchData();
        } catch {
          toast.error('Failed to save location');
        }
      },
      () => toast.error('Unable to get location')
    );
  };

  const handleSaveDraft = async () => {
    try {
      await surveysApi.update(survey.id, { summary, recommendations, conclusion });
      toast.success('Draft saved');
    } catch {
      toast.error('Failed to save draft');
    }
  };

  const handleComplete = async () => {
    if (!summary || !conclusion) {
      toast.error('Please provide summary and conclusion');
      return;
    }

    try {
      await surveysApi.complete(survey.id, summary, recommendations, conclusion, '');
      toast.success('Survey completed successfully');
      navigate('/jobs');
    } catch {
      toast.error('Failed to complete survey');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-900"></div>
      </div>
    );
  }

  if (!job || !survey) {
    return (
      <div className="p-4 text-center">
        <p>Survey not found or not yet assigned.</p>
        <Button onClick={() => navigate('/jobs')} className="mt-4">
          <ChevronLeft className="h-4 w-4 mr-2" /> Back to Jobs
        </Button>
      </div>
    );
  }

  const groupedElements = survey.elements.reduce((acc, elem) => {
    const category = elem.category || 'Other';
    if (!acc[category]) acc[category] = [];
    acc[category].push(elem);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-stone-50" data-testid="mobile-survey-page">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-emerald-900 text-white p-4">
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/jobs')} className="text-white hover:bg-emerald-800">
            <ChevronLeft className="h-4 w-4 mr-1" /> Back
          </Button>
          <div className="text-center">
            <p className="text-xs opacity-75">Job #{job.job_number}</p>
            <p className="font-medium text-sm">{job.title}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={handleGetLocation} className="text-white hover:bg-emerald-800">
            <MapPin className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start rounded-none border-b bg-white px-4">
          <TabsTrigger value="info">Property Info</TabsTrigger>
          <TabsTrigger value="elements">Elements</TabsTrigger>
          <TabsTrigger value="summary">Summary</TabsTrigger>
        </TabsList>

        {/* Property Info Tab */}
        <TabsContent value="info" className="p-4 space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Property Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm">
              <div>
                <span className="text-stone-500">Address:</span>
                <p className="font-medium">{job.property_address}</p>
              </div>
              <div>
                <span className="text-stone-500">Postcode:</span>
                <p className="font-medium">{job.property_postcode}</p>
              </div>
              {job.damage_type && (
                <div>
                  <span className="text-stone-500">Damage Type:</span>
                  <Badge className="ml-2 capitalize">{job.damage_type.replace('_', ' ')}</Badge>
                </div>
              )}
              {job.insurer && (
                <div>
                  <span className="text-stone-500">Insurer:</span>
                  <p className="font-medium">{job.insurer}</p>
                </div>
              )}
              {survey.gps_latitude && (
                <div>
                  <span className="text-stone-500">GPS:</span>
                  <p className="font-medium text-xs">{survey.gps_latitude.toFixed(6)}, {survey.gps_longitude.toFixed(6)}</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Elements Tab */}
        <TabsContent value="elements" className="p-4">
          <Accordion type="multiple" className="space-y-2">
            {Object.entries(groupedElements).map(([category, elements]) => (
              <AccordionItem key={category} value={category} className="bg-white rounded-lg border">
                <AccordionTrigger className="px-4 py-3 hover:no-underline">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{category}</span>
                    <Badge variant="outline" className="text-xs">{elements.length}</Badge>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-4 pb-4 space-y-4">
                  {elements.map((element) => (
                    <div key={element.element_id} className="border rounded-lg p-3 space-y-3 bg-stone-50">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-sm">{element.element_name}</span>
                        <Badge className={
                          CONDITION_RATINGS.find(c => c.id === element.condition)?.color || 'bg-gray-100'
                        }>
                          {element.condition?.replace('_', ' ')}
                        </Badge>
                      </div>

                      {/* Condition */}
                      <div className="space-y-1">
                        <Label className="text-xs">Condition</Label>
                        <Select 
                          value={element.condition} 
                          onValueChange={(val) => handleElementUpdate(element.element_id, 'condition', val)}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {CONDITION_RATINGS.map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      {element.condition === 'damaged' && (
                        <>
                          {/* Damage Type */}
                          <div className="space-y-1">
                            <Label className="text-xs">Damage Type</Label>
                            <Select 
                              value={element.damage_type || ''} 
                              onValueChange={(val) => handleElementUpdate(element.element_id, 'damage_type', val)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue placeholder="Select damage type" />
                              </SelectTrigger>
                              <SelectContent>
                                {DAMAGE_TYPES.map((t) => (
                                  <SelectItem key={t} value={t} className="capitalize">{t.replace('_', ' ')}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Severity */}
                          <div className="space-y-1">
                            <Label className="text-xs">Severity</Label>
                            <Select 
                              value={element.severity || 'none'} 
                              onValueChange={(val) => handleElementUpdate(element.element_id, 'severity', val)}
                            >
                              <SelectTrigger className="h-9">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {SEVERITY_LEVELS.map((s) => (
                                  <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Auto Description */}
                          {element.auto_description && (
                            <div className="p-2 bg-blue-50 rounded text-xs text-blue-800 border border-blue-200">
                              <strong>Suggested:</strong> {element.auto_description}
                            </div>
                          )}
                        </>
                      )}

                      {/* Manual Notes */}
                      <div className="space-y-1">
                        <Label className="text-xs">Notes</Label>
                        <Textarea
                          value={element.notes || ''}
                          onChange={(e) => handleElementUpdate(element.element_id, 'notes', e.target.value)}
                          rows={2}
                          className="text-sm"
                          placeholder="Additional observations..."
                        />
                      </div>
                    </div>
                  ))}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </TabsContent>

        {/* Summary Tab */}
        <TabsContent value="summary" className="p-4 space-y-4">
          <Card>
            <CardContent className="pt-4 space-y-4">
              <div className="space-y-2">
                <Label>Summary of Observations *</Label>
                <Textarea
                  value={summary}
                  onChange={(e) => setSummary(e.target.value)}
                  rows={4}
                  placeholder="Key findings from the inspection..."
                  data-testid="survey-summary-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Recommendations</Label>
                <Textarea
                  value={recommendations}
                  onChange={(e) => setRecommendations(e.target.value)}
                  rows={4}
                  placeholder="Recommended actions and repairs..."
                  data-testid="survey-recommendations-input"
                />
              </div>

              <div className="space-y-2">
                <Label>Conclusion *</Label>
                <Textarea
                  value={conclusion}
                  onChange={(e) => setConclusion(e.target.value)}
                  rows={4}
                  placeholder="Final assessment and conclusions..."
                  data-testid="survey-conclusion-input"
                />
              </div>
            </CardContent>
          </Card>

          <div className="flex gap-3">
            <Button variant="outline" onClick={handleSaveDraft} className="flex-1" data-testid="save-draft-btn">
              <Save className="h-4 w-4 mr-2" /> Save Draft
            </Button>
            <Button onClick={handleComplete} className="flex-1 bg-emerald-900 hover:bg-emerald-800" data-testid="complete-survey-btn">
              <Check className="h-4 w-4 mr-2" /> Complete Survey
            </Button>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
